// Perfiles de acceso del backoffice: que seccion ve cada cuenta y hasta donde.
//
// Un perfil (fila de `admin_roles`) es un mapa seccion → nivel:
//
//   hidden  la seccion no existe para esa cuenta: ni en el menu, ni por URL,
//           ni en el manual, ni en la API.
//   read    ve la pantalla y sus datos; los botones que cambian algo no
//           aparecen y el backend los rechaza.
//   write   ve y opera.
//
// El catalogo de secciones vive en shared/access/catalog.json porque lo leen
// tres cosas que no comparten codigo: este modulo (backend y portal), la
// CloudFront Function del manual (via Terraform) y el menu del manual (Astro).
// Aca se le agregan los tipos y las etiquetas; el orden de las secciones es
// el del catalogo y NO se reordena: el pase de lectura del manual codifica
// una letra por seccion en ese orden.
//
// Este modulo no importa nada de Node ni de React a proposito: lo compilan
// esbuild (Lambdas), Vite (admin-portal) y vitest.

import catalog from '../../shared/access/catalog.json' with { type: 'json' };

export const ACCESS_SECTIONS = catalog.sections as readonly AccessSection[];

export type AccessSection =
  | 'dashboard'
  | 'users'
  | 'partners'
  | 'catalog'
  | 'reports'
  | 'invoices'
  | 'jobs'
  | 'audit'
  | 'disposable'
  | 'team';

export const ACCESS_LEVELS = ['hidden', 'read', 'write'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export type AccessMap = Record<AccessSection, AccessLevel>;

/** Que significa cada seccion y cada nivel, para la pantalla de perfiles. */
export interface SectionMeta {
  label: string;
  /** Una linea: que hay ahi. */
  summary: string;
  /** Que se puede hacer con nivel `read`. */
  read: string;
  /** Que agrega el nivel `write`. `null` si la seccion es solo de lectura. */
  write: string | null;
  /** Si muestra datos personales de usuarios finales. */
  pii: boolean;
}

export const SECTION_META: Record<AccessSection, SectionMeta> = {
  dashboard: {
    label: 'Dashboard',
    summary: 'Estado general de cada plataforma: cuentas, suscripciones y actividad.',
    read: 'Ver los números.',
    write: null,
    pii: false,
  },
  users: {
    label: 'Usuarios',
    summary: 'Buscar cuentas de usuarios finales y abrir su ficha completa.',
    read: 'Buscar y abrir la ficha (datos personales, suscripción, dispositivos).',
    write: 'Bloquear, cerrar sesiones, quitar dispositivos, dar pase de gracia, reconciliar pagos, investigar correos.',
    pii: true,
  },
  partners: {
    label: 'Partners',
    summary: 'Las plataformas conectadas: configuración, planes, aplicaciones, términos.',
    read: 'Ver la lista y la configuración de cada plataforma.',
    write: 'Crear plataformas, editar su configuración, publicar planes y términos, rotar credenciales.',
    pii: false,
  },
  catalog: {
    label: 'Catálogo',
    summary: 'Referencias que los usuarios marcan como favoritas.',
    read: 'Ver tipos e ítems.',
    write: 'Crear, editar y archivar tipos e ítems.',
    pii: false,
  },
  reports: {
    label: 'Reportes',
    summary: 'Números contables del período.',
    read: 'Ver y exportar los reportes.',
    write: null,
    pii: false,
  },
  invoices: {
    label: 'Facturas',
    summary: 'Comprobantes emitidos por cada cobro.',
    read: 'Buscar facturas y abrir el comprobante.',
    write: 'Reintentar una emisión fallida y reenviar por correo.',
    pii: true,
  },
  jobs: {
    label: 'Jobs & DLQ',
    summary: 'Procesos automáticos y trabajo pendiente en las colas.',
    read: 'Ver el estado de los procesos y las colas.',
    write: 'Reprocesar la cola de descarte.',
    pii: false,
  },
  audit: {
    label: 'Audit log',
    summary: 'Quién hizo qué y cuándo.',
    read: 'Consultar el registro.',
    write: null,
    pii: false,
  },
  disposable: {
    label: 'Blacklist email',
    summary: 'Dominios de correo bloqueados al registrarse.',
    read: 'Ver la lista.',
    write: 'Agregar y quitar dominios.',
    pii: false,
  },
  team: {
    label: 'Equipo',
    summary: 'Las cuentas del propio backoffice y sus perfiles de acceso.',
    read: 'Ver quién tiene cuenta y con qué perfil.',
    write: 'Crear cuentas, cambiar perfiles y permisos, restablecer contraseñas, editar perfiles de acceso.',
    pii: false,
  },
};

function fill(level: AccessLevel, overrides: Partial<AccessMap> = {}): AccessMap {
  const out = {} as AccessMap;
  for (const s of ACCESS_SECTIONS) out[s] = overrides[s] ?? level;
  return out;
}

/**
 * Los tres perfiles que trae el sistema. Son el estado INICIAL de la tabla
 * `admin_roles` (migracion 0039) y el fallback para sesiones emitidas antes
 * de que la sesion llevara el mapa. `admin` es inmutable y lo ve todo.
 */
export const BUILTIN_ACCESS: Readonly<Record<'admin' | 'support' | 'readonly', AccessMap>> = {
  admin: fill('write'),
  // Soporte atiende usuarios: su ficha y sus facturas. Nada mas.
  support: fill('hidden', { users: 'write', invoices: 'read' }),
  // Mira el estado del sistema sin datos personales.
  readonly: fill('hidden', {
    dashboard: 'read',
    partners: 'read',
    catalog: 'read',
    jobs: 'read',
    audit: 'read',
  }),
};

export const ALL_HIDDEN: AccessMap = fill('hidden');

function isLevel(v: unknown): v is AccessLevel {
  return typeof v === 'string' && (ACCESS_LEVELS as readonly string[]).includes(v);
}

/**
 * Convierte un jsonb (o cualquier cosa) en un mapa completo. Lo que falte es
 * `hidden`; lo que no sea un nivel valido tambien. Una seccion nueva agregada
 * al catalogo queda oculta en todos los perfiles existentes hasta que alguien
 * la habilite — que es el default seguro.
 */
export function normalizeAccess(input: unknown): AccessMap {
  const src = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const out = {} as AccessMap;
  for (const s of ACCESS_SECTIONS) {
    const v = src[s];
    out[s] = isLevel(v) ? v : 'hidden';
  }
  return out;
}

const RANK: Record<AccessLevel, number> = { hidden: 0, read: 1, write: 2 };

/** `level` alcanza para lo que pide `required`. */
export function atLeast(level: AccessLevel, required: AccessLevel): boolean {
  return RANK[level] >= RANK[required];
}

export function canRead(access: AccessMap, section: AccessSection): boolean {
  return atLeast(access[section], 'read');
}

export function canWrite(access: AccessMap, section: AccessSection): boolean {
  return atLeast(access[section], 'write');
}

// ---------------------------------------------------------------------------
// Manual (/docs)
// ---------------------------------------------------------------------------

/**
 * Seccion a la que pertenece una pagina del manual, por prefijo del slug
 * (sin idioma). `null` = pagina abierta a cualquier cuenta con sesion
 * (introduccion, primeros pasos, mi cuenta, referencia). `danger_zone` no es
 * una seccion sino el permiso por cuenta que habilita cupones y zona critica.
 */
export type DocsGate = AccessSection | 'danger_zone';

const DOCS_PREFIXES = catalog.docs as readonly { prefix: string; section: DocsGate }[];

export function docsPageGate(slug: string): DocsGate | null {
  const clean = slug.replace(/^\/+/, '').replace(/\/+$/, '');
  for (const { prefix, section } of DOCS_PREFIXES) {
    if (prefix.endsWith('/')) {
      if (clean.startsWith(prefix)) return section;
    } else if (clean === prefix || clean.startsWith(`${prefix}/`)) {
      return section;
    }
  }
  return null;
}

/**
 * El pase de lectura del manual lleva el acceso codificado: una letra por
 * seccion (h/r/w) en el orden del catalogo, mas una `d` final si la cuenta
 * tiene `danger_zone`. Es lo unico que la CloudFront Function puede leer sin
 * consultar a nadie, y entra comodo en una cookie.
 */
const LEVEL_CHAR: Record<AccessLevel, string> = { hidden: 'h', read: 'r', write: 'w' };
const CHAR_LEVEL: Record<string, AccessLevel> = { h: 'hidden', r: 'read', w: 'write' };

export function encodeAccessForTicket(access: AccessMap, dangerZone: boolean): string {
  const letters = ACCESS_SECTIONS.map((s) => LEVEL_CHAR[access[s]]).join('');
  return dangerZone ? `${letters}d` : letters;
}

export function decodeAccessFromTicket(
  encoded: string,
): { access: AccessMap; dangerZone: boolean } | null {
  const m = /^([hrw]+)(d?)$/.exec(encoded);
  if (!m) return null;
  const letters = m[1] ?? '';
  const access = {} as AccessMap;
  ACCESS_SECTIONS.forEach((s, i) => {
    // Un pase mas corto que el catalogo (seccion agregada despues de emitido)
    // deja esa seccion oculta.
    access[s] = CHAR_LEVEL[letters.charAt(i)] ?? 'hidden';
  });
  return { access, dangerZone: m[2] === 'd' };
}
